"use client";

import {JSX} from "react";

import {useRouter} from "next/navigation";

import {TripMobileHeader} from "@/sections/trip/mobile-nav/trip-mobile-header";

import {clearTripSession} from "@/entities/trip";
import type {TripUnitPmsPropertyManagement} from "@/entities/trip/api/types";

import {useTranslation} from "@/shared/i18n";
import type {TranslationKey} from "@/shared/i18n/types";
import {ArrowRightIcon} from "@/shared/ui/icons/navigation/arrow-right-icon";
import {LogoBase} from "@/shared/ui/media/logos/logo-base";
import {Drawer} from "@/shared/ui/overlay/drawer";
import {assetPath} from "@/shared/utils/asset-path";
import {routes, TRIP_TABS, type TripTab} from "@/shared/utils/routing";

import "./styles/trip-mobile-menu.scss";

export interface TripMobileMenuProps {
    open: boolean;
    onClose: () => void;
    support: TripUnitPmsPropertyManagement;
    unitName: string;
    checkInDate: string;
    checkOutDate: string;
    onSelectTab: (tab: TripTab) => void;
}

const MENU_ITEMS: Array<{tab: TripTab; labelKey: TranslationKey}> = [
    {tab: TRIP_TABS.terms, labelKey: "trip.navRentalTerms"},
    {tab: TRIP_TABS.pricing, labelKey: "trip.navPricing"},
    {tab: TRIP_TABS.cancel, labelKey: "trip.navCancel"},
];

export function TripMobileMenu({open, onClose, support, unitName, checkInDate, checkOutDate, onSelectTab}: TripMobileMenuProps): JSX.Element {
    const {t} = useTranslation();
    const router = useRouter();

    const handleLogOut = (): void => {
        clearTripSession();
        router.push(routes.trip.list.url());
    };

    return (
        <Drawer open={open} onClose={onClose} direction="right" size="100%" enableBorderRadius={false} zIndex={1200}>
            <div className="trip-mobile-menu">
                <TripMobileHeader
                    support={support}
                    title={<LogoBase logoLink="/" imageLink={assetPath("/images/logos/casago-logo.svg")} width={96} height={32} alt="Casago" />}
                    onBack={onClose}
                />
                <div className="trip-mobile-menu__body">
                    <span className="trip-mobile-menu__eyebrow">{t("trip.yourTrip")}</span>
                    <div className="trip-mobile-menu__trip-card">
                        <span className="trip-mobile-menu__trip-name">{unitName}</span>
                        <span className="trip-mobile-menu__trip-dates">
                            {checkInDate} - {checkOutDate}
                        </span>
                    </div>

                    <nav className="trip-mobile-menu__list">
                        {MENU_ITEMS.map((item) => (
                            <button key={item.tab} type="button" className="trip-mobile-menu__row" onClick={() => onSelectTab(item.tab)}>
                                <span>{t(item.labelKey)}</span>
                                <ArrowRightIcon width={16} height={16} className="trip-mobile-menu__chevron" />
                            </button>
                        ))}
                    </nav>

                    <button type="button" className="trip-mobile-menu__link" onClick={handleLogOut}>
                        {t("trip.logOut")}
                    </button>
                </div>
            </div>
        </Drawer>
    );
}
